import { appLocale } from "../../locales/display.ts";
import { msg } from "../../locales/translate.ts";
export interface RollingReserveCurrency {
	currency: string;
	held: number;
	reserves?: number;
	nextRelease?: string;
}

export interface RollingReservePeriodCurrency {
	currency: string;
	taken: number;
	released: number;
	held: number;
}

export interface RollingReserveLifetimeCurrency {
	currency: string;
	taken: number;
	released: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const isAmount = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

// Lifetime totals are separate from both held balances and the MID breakdown.
// Older responses may omit lifetime; that is unavailable data, not zero flows.
export const parseRollingReserveLifetime = (data: unknown, clientId: number): RollingReserveLifetimeCurrency[] | null => {
	if (!isRecord(data) || data.client_id !== clientId) throw new Error(msg("unexpectedLifetimeReserveResponse"));
	if (data.lifetime === undefined || data.lifetime === null) return null;
	if (!isRecord(data.lifetime)) throw new Error(msg("invalidLifetimeReserves"));
	return Object.entries(data.lifetime).map(([currency, value]) => {
		if (!/^[A-Z]{3}$/.test(currency) || !isRecord(value) || !isAmount(value.taken) || !isAmount(value.released)) {
			throw new Error(msg("invalidLifetimeReserveAmounts"));
		}
		return { currency, taken: value.taken, released: value.released };
	}).sort((a, b) => a.currency.localeCompare(b.currency));
};

// taken/released are period flows; held is the cumulative balance at the end of the period.
export const parseRollingReservePeriod = (data: unknown, clientId: number | undefined, from: string, to: string): RollingReservePeriodCurrency[] => {
	if (!isRecord(data) || (clientId !== undefined ? data.client_id !== clientId : data.client_id !== undefined && data.client_id !== null)
		|| data.from !== from || data.to !== to || !isRecord(data.currencies)) {
		throw new Error(msg("unexpectedPeriodReserveResponse"));
	}
	return Object.entries(data.currencies).map(([currency, value]) => {
		if (!/^[A-Z]{3}$/.test(currency) || !isRecord(value) || !isAmount(value.taken) || !isAmount(value.released) || !isAmount(value.held)) {
			throw new Error(msg("invalidPeriodReserveAmounts"));
		}
		return { currency, taken: value.taken, released: value.released, held: value.held };
	}).sort((a, b) => a.currency.localeCompare(b.currency));
};

// Client totals come from top-level currencies; mids is a breakdown, not extra reserves.
// Empty currencies is a valid response; each currency contains held, reserves and next_release.
// Never infer a balance from period flows.
export const parseRollingReserves = (data: unknown, clientId: number): RollingReserveCurrency[] => {
	if (!data || typeof data !== "object") throw new Error(msg("missingReserveData"));
	const result = data as { client_id?: unknown; currencies?: unknown };
	if (result.client_id !== clientId || !result.currencies || typeof result.currencies !== "object" || Array.isArray(result.currencies)) {
		throw new Error(msg("unexpectedReserveResponse"));
	}
	return Object.entries(result.currencies).map(([currency, value]) => {
		const held = value && typeof value === "object" ? (value as { held?: unknown }).held : undefined;
		if (!/^[A-Z]{3}$/.test(currency) || typeof held !== "number" || !Number.isFinite(held)) {
			throw new Error(msg("invalidReserveBalance"));
		}
		const details = value as { reserves?: unknown; next_release?: unknown };
		const reserves = typeof details.reserves === "number" && Number.isSafeInteger(details.reserves) && details.reserves >= 0 ? details.reserves : undefined;
		const nextRelease = formatReserveAsOf(details.next_release)?.split(" ")[0];
		return { currency, held, ...(reserves !== undefined ? { reserves } : {}), ...(nextRelease ? { nextRelease } : {}) };
	}).sort((a, b) => a.currency.localeCompare(b.currency));
};

export const formatReserveAmount = (amount: number) => new Intl.NumberFormat(appLocale("en-GB"), {
	minimumFractionDigits: 2, maximumFractionDigits: 4,
}).format(amount);

// The API timestamp has no timezone: display server time without browser conversion.
export const formatReserveAsOf = (value: unknown): string | undefined => {
	if (typeof value !== "string") return undefined;
	const match = /^(\d{4})-(\d{2})-(\d{2}) ([0-2]\d:[0-5]\d:[0-5]\d)$/.exec(value);
	if (!match || Number(match[2]) < 1 || Number(match[2]) > 12 || Number(match[3]) < 1 || Number(match[3]) > 31 || Number(match[4].slice(0, 2)) > 23) return undefined;
	return `${match[3]}.${match[2]}.${match[1]} ${match[4]}`;
};
